import { useStoreApi } from '@xyflow/react';
import { useEffect } from 'react';

import { useLocalizationStore } from '../../localization/localizationStore';
import { useEditCanvasStore } from '../../store';
import { selectDeletableCanvasNodes } from '../../utils/selectDeletableCanvasNodes';
import { shouldApplyCanvasDeleteShortcut } from '../../utils/shouldApplyCanvasDeleteShortcut';
import { commands } from '../commands';
import { useCommandHistory } from '../commands/hooks/useCommandHistory';

/**
 * Owns Backspace/Delete for selected canvas nodes. React Flow's `deleteKeyCode` is left
 * disarmed: its input check misses composer chips, and it treats document-wide Backspace
 * as canvas delete even when the key came from chat or body after a chip unmounts.
 * We only apply when the key originated on `.react-flow` (box-select focuses a node).
 */
export const useCanvasDeleteShortcut = () => {
  const execute = useCommandHistory((state) => state.execute);
  const canvasMode = useEditCanvasStore((state) => state.canvasMode);
  const readOnly = useEditCanvasStore((state) => state.readOnly);
  const localizationActive = useLocalizationStore((state) => state.active);
  const reactFlowStore = useStoreApi();

  useEffect(() => {
    if (readOnly || localizationActive || canvasMode.mode !== 'default') {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Backspace' && event.key !== 'Delete') {
        return;
      }

      if (!shouldApplyCanvasDeleteShortcut(event)) {
        return;
      }

      const reactFlowSelectedIds = new Set(
        reactFlowStore
          .getState()
          .nodes.filter((node) => node.selected)
          .map((node) => node.id),
      );
      // setCanvasMode writes `selected` on the edit-canvas store; React Flow copies
      // it in StoreUpdater's effect. Union so Backspace works immediately after Back.
      const nodesToDelete = selectDeletableCanvasNodes(useEditCanvasStore.getState().nodes, reactFlowSelectedIds);

      if (nodesToDelete.length === 0) {
        return;
      }

      event.preventDefault();
      execute(commands.removeNodes(nodesToDelete));
    };

    // Capture so composedPath is read before ProseMirror unmounts a composer chip on this keydown.
    document.addEventListener('keydown', onKeyDown, true);

    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [canvasMode.mode, execute, localizationActive, reactFlowStore, readOnly]);
};
